import { createRef, type InputHTMLAttributes, type RefObject } from 'react';
import { classNames } from '~/utils/styles';
import Icon, { IconNames } from './Icon';

export type SwitchInputProps = {
    unmask?: boolean;
    className?: string;
    checked?: boolean;
    onChange?: (value: boolean) => void;
    ref?: RefObject<HTMLInputElement | null>;
    'data-test-component'?: string;
    'data-test-name'?: string;
} & Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'checked' | 'data-test-component' | 'data-test-name'
>;

export function SwitchInput({
    onChange,
    unmask = false,
    className = '',
    checked = false,
    ref,
    'data-test-component': testComponent = 'ui/forms/SwitchInput',
    'data-test-name': testName,
    ...props
}: SwitchInputProps) {
    const inputRef = ref ?? createRef<HTMLInputElement>();

    return (
        <div
            className={classNames({
                'switch-control cursor-pointer': true,
                'switch-control-on': checked,
                [className]: Boolean(className),
                'fs-unmask': unmask,
            })}
            onClick={(e) => {
                // ignore input’s own click when bubbles back up after inputRef.current?.click()
                // ignore also the click when readOnly is set. Notice this is not the same readonly for the input, this
                // is to make the element to display a state but not to be interactive.
                if (e.target === inputRef.current || props.readOnly) return;
                inputRef.current?.click();
            }}
            data-test-component={testComponent}
            data-test-name={testName}
            data-test-value={checked}>
            <input
                {...props}
                ref={inputRef}
                type='checkbox'
                className={unmask ? 'fs-unmask' : ''}
                onChange={() => onChange?.(!checked)}
                checked={checked}
                readOnly
            />
        </div>
    );
}
export type SwitchProps = {
    value: boolean;
    disabled?: boolean;
    label?: string;
    icon?: IconNames;
    onChange?: (value: boolean) => void;
    className?: string;
    bordered?: boolean;
    labelPosition?: 'left' | 'right';
    id?: string;
    name?: string;
    'data-test-name'?: string;
    'data-test-component'?: string;
};

export default function Switch({
    value,
    disabled = false,
    label,
    icon,
    onChange = () => null,
    className = '',
    bordered = false,
    labelPosition = 'left',
    id,
    'data-test-component': testComponent = 'ui/forms/Switch',
    'data-test-name': testName,
}: SwitchProps) {
    return (
        <div
            className={classNames({
                [`switch-field switch-field-${labelPosition}`]: true,
                [className]: Boolean(className),
                'switch-field-bordered': bordered,
                disabled,
            })}
            onClick={() => {
                if (!disabled) {
                    onChange(!value);
                }
            }}
            data-test-component={testComponent}
            data-test-name={testName}>
            {icon && labelPosition === 'left' && bordered && (
                <Icon
                    className='icon-always-first'
                    icon={icon}></Icon>
            )}
            {label && <span className='switch-label'>{label}</span>}
            <SwitchInput
                checked={value}
                id={id}
                disabled={disabled}
                unmask={false}
                readOnly
            />
        </div>
    );
}
